import { Capacitor } from '@capacitor/core';

import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { Router } from '@angular/router';

import { TranslitPipe } from '@app/pipes/translit.pipe';
import { PushTokensService } from './push-tokens.service';
import { UtilsService } from './utils.service';

/**
 * Firebase Cloud Messaging for the native Android build.
 *
 * The plugin is imported lazily so that the browser bundle and the SSR build
 * never evaluate the native bridge. On web and on the server `init()` is a
 * no-op and the token stays null.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
  private router = inject(Router);
  private pushTokensService = inject(PushTokensService);
  private translitPipe = new TranslitPipe();

  /** Must match default_notification_channel_id in the Android resources. */
  private readonly channelId = 'galerija_obavestenja';

  /** FCM registration token of this device, null until registration succeeds. */
  public readonly token = signal<string | null>(null);

  public isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  async init(): Promise<void> {
    if (!this.isNative()) {
      return;
    }

    const { PushNotifications } = await import('@capacitor/push-notifications');

    let status = await PushNotifications.checkPermissions();

    if (status.receive === 'prompt') {
      status = await PushNotifications.requestPermissions();
    }

    if (status.receive !== 'granted') {
      return;
    }

    if (Capacitor.getPlatform() === 'android') {
      await PushNotifications.createChannel({
        id: this.channelId,
        name: 'Обавештења',
        description: 'Обавештења Галерије српске историје',
        importance: 4,
        visibility: 1,
      });
    }

    await PushNotifications.removeAllListeners();

    await PushNotifications.addListener('registration', (token) => {
      this.token.set(token.value);

      if (isDevMode()) {
        console.info('FCM token', token.value);
      }

      this.pushTokensService.saveToken(token.value, Capacitor.getPlatform());
    });

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('FCM registration failed', error);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const message = notification.title ?? notification.body;

      if (message) {
        UtilsService.showSnackbar(this.translitPipe.transform(message));
      }
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const route = action.notification.data?.['route'];

      if (typeof route === 'string' && route.startsWith('/')) {
        this.router.navigateByUrl(route);
      }
    });

    await PushNotifications.register();
  }
}
